import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  Output,
} from '@angular/core';

import { Popup } from '../popup/popup';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ExcelImportService,
  ExcelParseResult,
} from '../../services/excel-import.service';

/**
 * Reusable Excel-upload control: a "File Upload" button that opens
 * a popup, lets the user pick an .xlsx file and PARSES it. The
 * parsed result is emitted via (parsed); the parent then applies it
 * to its own form model (so the model reference is always current).
 *
 * The heavy parsing/matching lives in ExcelImportService; this
 * component only owns the button + popup UI and the loading state.
 */
@Component({
  selector: 'app-excel-upload',
  standalone: true,
  imports: [CommonModule, Popup, TranslatePipe],
  templateUrl: './excel-upload.html',
})
export class ExcelUpload {
  /** Target sheet name, e.g. "Balance Sheet". */
  @Input() sheetName = '';
  /** Region start marker label, e.g. "Balance Sheet". */
  @Input() startMarker = '';
  /** Optional region end markers (next statement/section). */
  @Input() endMarkers: string[] = [];
  /** Form sections (read-only here: used for sheet/label detection). */
  @Input() sections: any[] = [];

  /**
   * Emitted after a file is parsed. `null` means no matching sheet
   * / no data was found in the file.
   */
  @Output() parsed = new EventEmitter<ExcelParseResult | null>();

  showPopup = false;
  selectedFile: File | null = null;
  selectedFileName = '';
  isImporting = false;
  popupError = '';

  constructor(
    private excelImport: ExcelImportService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  open(): void {
    // Always open the popup empty (no carried-over file).
    this.popupError = '';
    this.selectedFile = null;
    this.selectedFileName = '';
    this.isImporting = false;
    this.showPopup = true;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    const file =
      input.files && input.files.length ? input.files[0] : null;

    this.selectedFile = file;
    this.selectedFileName = file ? file.name : '';
  }

  async onSave(fileInput?: HTMLInputElement): Promise<void> {
    if (this.isImporting) {
      return;
    }

    /*
     * Read the file straight from the <input> element so the very
     * first Save click works, without depending on the (change)
     * event having updated selectedFile.
     */
    const file =
      (fileInput?.files && fileInput.files.length
        ? fileInput.files[0]
        : null) ?? this.selectedFile;

    if (!file) {
      this.popupError = 'EXCEL_UPLOAD.SELECT_FILE_FIRST';
      return;
    }

    this.isImporting = true;
    this.popupError = '';
    this.cdr.detectChanges();

    try {
      const result = await this.excelImport.parse(
        file,
        {
          sheetName: this.sheetName,
          startMarker: this.startMarker,
          endMarkers: this.endMarkers,
        },
        this.sections,
      );

      /*
       * ExcelJS resolves outside Angular's zone, so re-enter it so
       * the parent's (parsed) handler + its change detection run.
       */
      this.zone.run(() => {
        this.isImporting = false;
        this.showPopup = false;
        this.selectedFile = null;
        this.selectedFileName = '';
        this.parsed.emit(result);
      });
    } catch (error) {
      console.error('Excel import failed:', error);

      this.zone.run(() => {
        this.isImporting = false;
        this.popupError = 'EXCEL_UPLOAD.UNABLE_TO_READ';
      });
    } finally {
      this.cdr.detectChanges();
    }
  }

  close(): void {
    if (this.isImporting) {
      return;
    }

    this.showPopup = false;
    this.selectedFile = null;
    this.selectedFileName = '';
  }
}
